import { useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';

import { useScroller } from 'src/hooks/use-scroller';
import { useResponsive } from 'src/hooks/use-responsive';

import { getMaxPrice } from 'src/utils/format-number';

import { getData } from 'src/lib/firebase';

import { useSearchProductStore } from 'src/components/search';

import { useAuthContext } from 'src/auth/hooks';

import { getOrderBy, getFilters, getProduct, promotionDataFormatter } from '../product-ssr';

// ----------------------------------------------------------------------

export function useGetProducts(list, sortBy, currentProducts, searchParams) {
  const { user } = useAuthContext();

  const { bottomReached } = useScroller();

  const smDown = useResponsive('down', 'sm');

  const { shipping, category, priceRange } = searchParams;

  const MAX_VALUE = getMaxPrice(list === 'search' ? searchResults : currentProducts);

  const { searchResults, searchLoading, setSearchLoading } = useSearchProductStore();

  const filters = useSetState({
    rating: '',
    gender: [],
    colors: [],
    shipping: !!shipping,
    category: category ?? 'all',
    priceRange: priceRange?.split('-').map(Number) ?? [0, MAX_VALUE],
  });

  const { state, setState } = useSetState({
    lastVisible: null,
    loadingMore: false,
    maxValue: MAX_VALUE,
    loading: list === 'search',
    allProducts: currentProducts,
    index: list === 'search' ? 0 : currentProducts.length + 1,
  });

  const fetchProducts = async (index = 0, productArray = [], prevAllProducts = []) => {
    const promotions =
      list === 'search'
        ? productArray.slice(index, index + 21)
        : await getData({
            path: list === 'favorites' ? `users/${user?.id}/favorites` : 'promotions',
            formatter: (promotion) =>
              list === 'favorites' ? getProduct(promotion.id) : promotionDataFormatter(promotion),
            defaultData: [],
            options: {
              limit: 21,
              startAfter: state.lastVisible,
              orderBy: getOrderBy(list === 'favorites' ? 'newest' : sortBy),
              where:
                list === 'favorites'
                  ? []
                  : [
                      { field: 'publish', operator: '==', value: 'YES' },
                      ...getFilters(searchParams),
                    ],
            },
          });

    const total = promotions.length;
    const allProducts = [...prevAllProducts, ...promotions.slice(0, total === 21 ? -1 : total)];
    const newMaxValue = getMaxPrice(allProducts);

    if (list === 'search') {
      setSearchLoading();
      filters.setState({ priceRange: [0, newMaxValue] });
    }

    setState({
      allProducts,
      loading: false,
      loadingMore: false,
      maxValue: newMaxValue,
      index: index + total - (index ? 1 : 0),
      lastVisible: list === 'search' ? null : promotions[total - (total === 21 ? 2 : 1)],
    });
  };

  const loadMoreProducts = () => {
    if (!state.loadingMore && state.allProducts.length > state.index && state.index >= 20) {
      setState({ loadingMore: true });
      fetchProducts(state.index, list === 'search' ? searchResults : [], state.allProducts);
    }
  };

  useEffect(() => {
    if (!searchLoading && list === 'search') {
      setState({ loading: true });
      fetchProducts(0, searchResults, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, searchResults, searchLoading]);

  useEffect(() => {
    if (list === 'favorites' || list === 'shop') {
      filters.setState({ priceRange: [0, getMaxPrice(currentProducts)] });
      setState({
        loading: false,
        allProducts: currentProducts,
        index: currentProducts.length + 1,
        maxValue: getMaxPrice(currentProducts),
        lastVisible: currentProducts[currentProducts.length - 1],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, filters.setState, currentProducts]);

  useEffect(() => {
    if (smDown && bottomReached) loadMoreProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottomReached]);

  return {
    filters,
    loadMoreProducts,
    showing: state.index,
    loading: state.loading,
    otherCondition: !smDown,
    maxValue: state.maxValue,
    allProducts: state.allProducts,
    loadingMore: state.loadingMore,
    totalProducts: state.allProducts.length,
    setLoading: (loading = true) => setState({ loading }),
    productsEmpty: !state.loading && !state.allProducts.length,
  };
}
